import { useCallback, useState } from 'react';

import SecondaryPageLayout from '@/Layout/SecondaryPageLayout';
import { createWebAuthnRegistration, submitInteraction } from '@/apis/experience';
import Button from '@/components/Button';
import TextLink from '@/components/TextLink';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import useWebAuthnOperation from '@/hooks/use-webauthn-operation';

import styles from './index.module.scss';

const PasskeyPrompt = () => {
  const asyncCreateRegistration = useApi(createWebAuthnRegistration);
  const asyncSubmitInteraction = useApi(submitInteraction);
  const handleWebAuthn = useWebAuthnOperation();
  const handleError = useErrorHandler();
  const redirectTo = useGlobalRedirectTo();

  const [isAdding, setIsAdding] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);

  const skip = useCallback(async () => {
    setIsSkipping(true);
    const [error, result] = await asyncSubmitInteraction();
    if (error) {
      await handleError(error);
      setIsSkipping(false);
      return;
    }
    if (result) {
      await redirectTo(result.redirectTo);
    }
  }, [asyncSubmitInteraction, handleError, redirectTo]);

  const addPasskey = useCallback(async () => {
    setIsAdding(true);
    const [error, registration] = await asyncCreateRegistration();
    if (error) {
      await handleError(error);
      setIsAdding(false);
      return;
    }
    if (!registration) {
      setIsAdding(false);
      return;
    }
    // HandleWebAuthn calls navigator.credentials.create() then bindMfa
    // (which performs /web-authn/registration/verify + /profile/mfa +
    // submitInteraction internally). On success the user is redirected.
    await handleWebAuthn(registration.options, registration.verificationId);
    setIsAdding(false);
  }, [asyncCreateRegistration, handleError, handleWebAuthn]);

  return (
    <SecondaryPageLayout title="mfa.create_a_passkey">
      <div className={styles.container}>
        <p className={styles.description}>
          Add a passkey now and use Touch ID, Face ID, Windows Hello, or your security key for
          faster sign-in next time. You can skip and continue with the current method.
        </p>

        <Button
          title="mfa.create_a_passkey"
          type="primary"
          size="large"
          isLoading={isAdding}
          isDisabled={isAdding || isSkipping}
          onClick={addPasskey}
        />

        <TextLink className={styles.skip} onClick={skip}>
          Skip for now
        </TextLink>
      </div>
    </SecondaryPageLayout>
  );
};

export default PasskeyPrompt;
